import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { profile } from '../data/portfolio';
import { MaskedLines, EASE } from './Reveal';
import { scrollToId } from './scroll';

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
    const y = useTransform(scrollYProgress, [0, 1], [0, -120]);
    const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section ref={ref} id="top" data-testid="hero-section" className="relative z-10 flex min-h-[100svh] flex-col justify-center overflow-hidden">
            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(100deg,rgba(8,9,12,0.94)_0%,rgba(8,9,12,0.8)_38%,rgba(8,9,12,0.35)_62%,transparent_82%)]" />

            <motion.div
                style={{ y, opacity }}
                className="relative z-10 mx-auto grid w-full max-w-[1400px] grid-cols-1 items-center gap-10 px-6 pb-24 pt-28 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-4"
            >
                <div className="min-w-0">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
                        className="mb-7 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-mint beacon-pulse" />
                        <span className="font-mono text-[10px] tracking-[0.28em] text-mist md:text-[11px]">
                            {profile.roles}
                        </span>
                    </motion.div>

                    <MaskedLines
                        as="h1"
                        delay={0.25}
                        className="font-display font-semibold leading-[0.98] tracking-tight text-snow"
                        lineClassName="text-[clamp(2.6rem,9.5vw,9.5rem)]"
                        lines={['UJJWAL', 'PRAVEEN']}
                    />

                    <MaskedLines
                        as="p"
                        delay={0.6}
                        className="mt-6 max-w-xl"
                        lineClassName="font-sub text-base text-mist md:text-lg [text-shadow:0_2px_20px_rgba(8,9,12,0.95)]"
                        lines={[profile.tagline]}
                    />

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 0.85 }}
                        className="mt-4 max-w-lg text-sm leading-relaxed text-mist/80 md:text-[15px] [text-shadow:0_2px_18px_rgba(8,9,12,0.95)]"
                    >
                        {profile.intro}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 1 }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-cta-journey"
                            onClick={() => scrollToId('journey')}
                            className="rounded-full bg-neon px-7 py-3 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-ink transition-transform hover:scale-[1.03] active:scale-95"
                        >
                            Walk the journey
                        </button>
                        <a
                            data-testid="hero-cta-resume"
                            href="/Ujjwal_Praveen_Resume.pdf"
                            download="Ujjwal_Praveen_Resume.pdf"
                            className="rounded-full border border-white/15 px-7 py-3 font-mono text-xs uppercase tracking-[0.18em] text-snow transition-all hover:border-neon/60 hover:text-neon"
                        >
                            Download résumé
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.3 }}
                        className="mt-12 hidden font-mono text-[10px] tracking-[0.3em] text-mist/50 md:block"
                    >
                        9 NODES · 1 PATH · SKYLINE UNDER CONSTRUCTION
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, ease: EASE, delay: 1.1 }}
                    className="flex flex-col items-center gap-3 justify-self-center"
                    data-testid="hero-avatar"
                >
                    <div className="glass rounded-2xl px-5 py-2 font-mono text-[11px] tracking-[0.18em] text-neon">
                        Hi! I am Ujjwal
                    </div>
                    <span className="h-4 w-px bg-neon/50" />
                    <div className="overflow-hidden rounded-[2rem] border border-neon/25 shadow-[0_0_70px_-18px_rgba(0,242,254,0.55)]">
                        <img
                            src="/avatar.png"
                            alt="Clay-style avatar of Ujjwal Praveen waving hello"
                            className="h-[240px] w-[192px] object-cover object-top lg:h-[320px] lg:w-[256px]"
                        />
                    </div>
                </motion.div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                className="pointer-events-none absolute bottom-7 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-3 md:flex"
            >
                <span className="font-mono text-[9px] uppercase tracking-[0.34em] text-mist/60">
                    {profile.scrollCue}
                </span>
                <span className="scroll-cue block h-10 w-px overflow-hidden bg-white/10">
                    <span className="block h-full w-full bg-neon" />
                </span>
            </motion.div>
        </section>
    );
}
